/**
 * Comprehensive Academic Grades Management Page
 * Provides complete grades management with data-rich interface
 */

import React, { useState, useEffect } from 'react';
import GradesService from '../../lib/services/gradesService';
import './GradesManagementPage.css';

const GradesManagementPage = ({ onBack, onTabChange, context = {}, currentUser, canCreate = true, canEdit = true, role = 'teacher' }) => {
  const [gradesData, setGradesData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'grades', 'students', 'statistics'
  const [selectedClass, setSelectedClass] = useState(context.classId || null);
  const [selectedSubject, setSelectedSubject] = useState(context.subject || null);
  const [selectedStudent, setSelectedStudent] = useState(context.studentId || null);
  const [gradesService] = useState(new GradesService('tn_01'));

  useEffect(() => {
    fetchGradesData();
  }, [selectedClass, selectedSubject, selectedStudent]);

  const fetchGradesData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching grades data...', { selectedClass, selectedSubject, selectedStudent });
      
      const { data, error: fetchError } = await gradesService.getGradesData(selectedClass, selectedSubject, selectedStudent);
      
      if (fetchError) {
        console.error('Error fetching grades data:', fetchError);
        setError(fetchError);
        return;
      }
      
      if (!data) {
        console.error('No grades data returned');
        setError('No grades data available');
        return;
      }
      
      setGradesData(data);
      console.log('Grades data loaded successfully:', data);
      
    } catch (err) {
      console.error('Unexpected error in fetchGradesData:', err);
      setError(err.message || 'Failed to fetch grades data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGrade = () => {
    // This would open a create grade modal
    console.log('Create grade clicked');
  };

  const handleEditGrade = (grade) => {
    // This would open an edit grade modal
    console.log('Edit grade clicked:', grade);
  };

  const handleDeleteGrade = async (gradeId) => {
    if (window.confirm('Are you sure you want to delete this grade?')) {
      try {
        const { error } = await gradesService.deleteGrade(gradeId);
        if (error) {
          console.error('Error deleting grade:', error);
          return;
        }
        await fetchGradesData(); // Refresh data
      } catch (err) {
        console.error('Error deleting grade:', err);
      }
    }
  };

  const getGradeColor = (grade) => {
    if (!grade || isNaN(grade)) return '#6b7280';
    
    if (grade >= 90) return '#10b981'; // Green for A
    if (grade >= 80) return '#3b82f6'; // Blue for B
    if (grade >= 70) return '#f59e0b'; // Yellow for C
    if (grade >= 60) return '#f97316'; // Orange for D
    return '#ef4444'; // Red for F
  };

  const getGradeLevel = (grade) => {
    if (!grade || isNaN(grade)) return 'N/A';
    
    if (grade >= 90) return 'A';
    if (grade >= 80) return 'B';
    if (grade >= 70) return 'C';
    if (grade >= 60) return 'D';
    return 'F';
  };

  const getPassingStatus = (grade) => {
    return grade && !isNaN(grade) && grade >= 60 ? 'Passing' : 'Failing';
  };

  if (loading) {
    return (
      <div className="grades-management-page">
        <div className="page-header">
          <button className="back-button" onClick={onBack}>← 返回主頁</button>
          <h1>學業成績</h1>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>載入成績資料中...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="grades-management-page">
        <div className="page-header">
          <button className="back-button" onClick={onBack}>← 返回主頁</button>
          <h1>學業成績</h1>
        </div>
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <h3>載入錯誤</h3>
          <p>{error}</p>
          <button className="retry-button" onClick={fetchGradesData}>重試</button>
        </div>
      </div>
    );
  }

  return (
    <div className="grades-management-page">
      {/* Header */}
      <div className="page-header">
        <button className="back-button" onClick={onBack}>← 返回主頁</button>
        <h1>學業成績</h1>
        <button className="refresh-button" onClick={fetchGradesData} title="重新載入">
          🔄
        </button>
      </div>

      {/* Navigation Tabs */}
      <div className="grades-tabs">
        <button
          className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          概覽
        </button>
        <button
          className={`tab-btn ${activeTab === 'grades' ? 'active' : ''}`}
          onClick={() => setActiveTab('grades')}
        >
          成績列表
        </button>
        <button
          className={`tab-btn ${activeTab === 'students' ? 'active' : ''}`}
          onClick={() => setActiveTab('students')}
        >
          學生排名
        </button>
        <button
          className={`tab-btn ${activeTab === 'statistics' ? 'active' : ''}`}
          onClick={() => setActiveTab('statistics')}
        >
          統計分析
        </button>
      </div>

      {/* Content */}
      <div className="page-content">
        {activeTab === 'overview' && (
          <div className="overview-section">
            {/* Summary Cards */}
            <div className="summary-cards">
              <div className="summary-card">
                <div className="card-icon">📊</div>
                <div className="card-content">
                  <h3>總成績數</h3>
                  <p className="card-value">{gradesData.totalGrades}</p>
                </div>
              </div>
              <div className="summary-card">
                <div className="card-icon">✅</div>
                <div className="card-content">
                  <h3>及格成績</h3>
                  <p className="card-value">{gradesData.passingGrades}</p>
                </div>
              </div>
              <div className="summary-card">
                <div className="card-icon">❌</div>
                <div className="card-content">
                  <h3>不及格成績</h3>
                  <p className="card-value">{gradesData.failingGrades}</p>
                </div>
              </div>
              <div className="summary-card">
                <div className="card-icon">📈</div>
                <div className="card-content">
                  <h3>平均分</h3>
                  <p className="card-value">{gradesData.averageGrade.toFixed(1)}</p>
                </div>
              </div>
            </div>

            {/* Grade Distribution */}
            <div className="grade-distribution">
              <h2>成績分布</h2>
              <div className="distribution-chart">
                <div className="distribution-item">
                  <span className="grade-level A">A (90-100)</span>
                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width: '25%' }}></div>
                  </div>
                  <span className="count">25%</span>
                </div>
                <div className="distribution-item">
                  <span className="grade-level B">B (80-89)</span>
                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width: '30%' }}></div>
                  </div>
                  <span className="count">30%</span>
                </div>
                <div className="distribution-item">
                  <span className="grade-level C">C (70-79)</span>
                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width: '25%' }}></div>
                  </div>
                  <span className="count">25%</span>
                </div>
                <div className="distribution-item">
                  <span className="grade-level D">D (60-69)</span>
                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width: '15%' }}></div>
                  </div>
                  <span className="count">15%</span>
                </div>
                <div className="distribution-item">
                  <span className="grade-level F">F (0-59)</span>
                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width: '5%' }}></div>
                  </div>
                  <span className="count">5%</span>
                </div>
              </div>
            </div>

            {/* Recent Grades */}
            <div className="recent-grades">
              <h2>最近成績</h2>
              <div className="grades-grid">
                {gradesData.recentGrades.map((grade, index) => (
                  <div key={index} className="grade-card">
                    <div className="grade-header">
                      <h4>{grade.studentName}</h4>
                      <span 
                        className="grade-badge"
                        style={{ backgroundColor: getGradeColor(grade.grade) }}
                      >
                        {grade.grade}
                      </span>
                    </div>
                    <div className="grade-details">
                      <p><strong>科目:</strong> {grade.subject}</p>
                      <p><strong>班別:</strong> {grade.className}</p>
                      <p><strong>等級:</strong> {getGradeLevel(grade.grade)}</p>
                      <p><strong>狀態:</strong> {getPassingStatus(grade.grade)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="quick-actions">
              <h2>快速操作</h2>
              <div className="action-buttons">
                {canCreate && (
                  <button className="action-btn primary" onClick={handleCreateGrade}>
                    ➕ 新增成績
                  </button>
                )}
                <button className="action-btn" onClick={() => setActiveTab('grades')}>
                  📋 查看所有成績
                </button>
                <button className="action-btn" onClick={() => setActiveTab('statistics')}>
                  📊 查看統計
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'grades' && (
          <div className="grades-section">
            <div className="section-header">
              <h2>成績列表</h2>
              {canCreate && (
                <button className="action-btn primary" onClick={handleCreateGrade}>
                  ➕ 新增成績
                </button>
              )}
            </div>
            
            <div className="grades-table">
              <table>
                <thead>
                  <tr>
                    <th>學生姓名</th>
                    <th>科目</th>
                    <th>班別</th>
                    <th>成績</th>
                    <th>等級</th>
                    <th>狀態</th>
                    <th>日期</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {gradesData.grades.map((grade, index) => (
                    <tr key={index}>
                      <td>
                        <div className="student-info">
                          <div className="student-avatar">
                            {grade.studentName?.charAt(0) || '?'}
                          </div>
                          <span>{grade.studentName}</span>
                        </div>
                      </td>
                      <td>{grade.subject}</td>
                      <td>{grade.className}</td>
                      <td>
                        <span 
                          className="grade-value"
                          style={{ color: getGradeColor(grade.grade) }}
                        >
                          {grade.grade}
                        </span>
                      </td>
                      <td>
                        <span 
                          className="grade-level-badge"
                          style={{ backgroundColor: getGradeColor(grade.grade) }}
                        >
                          {getGradeLevel(grade.grade)}
                        </span>
                      </td>
                      <td>
                        <span 
                          className={`status-badge ${getPassingStatus(grade.grade).toLowerCase()}`}
                        >
                          {getPassingStatus(grade.grade)}
                        </span>
                      </td>
                      <td>{new Date(grade.created_at).toLocaleDateString()}</td>
                      <td>
                        {canEdit && (
                          <div className="action-buttons">
                            <button 
                              className="action-btn small"
                              onClick={() => handleEditGrade(grade)}
                            >
                              編輯
                            </button>
                            <button 
                              className="action-btn small danger"
                              onClick={() => handleDeleteGrade(grade.id)}
                            >
                              刪除
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'students' && (
          <div className="students-section">
            <h2>學生排名</h2>
            
            <div className="students-table">
              <table>
                <thead>
                  <tr>
                    <th>排名</th>
                    <th>學生姓名</th>
                    <th>班別</th>
                    <th>平均分</th>
                    <th>總成績數</th>
                    <th>及格率</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.values(gradesData.studentGroups)
                    .sort((a, b) => b.averageGrade - a.averageGrade)
                    .map((student, index) => (
                    <tr key={index}>
                      <td>
                        <span className="rank-badge">
                          #{index + 1}
                        </span>
                      </td>
                      <td>
                        <div className="student-info">
                          <div className="student-avatar">
                            {student.studentName?.charAt(0) || '?'}
                          </div>
                          <span>{student.studentName}</span>
                        </div>
                      </td>
                      <td>{student.className || 'Unknown'}</td>
                      <td>
                        <span 
                          className="average-grade"
                          style={{ color: getGradeColor(student.averageGrade) }}
                        >
                          {student.averageGrade.toFixed(1)}
                        </span>
                      </td>
                      <td>{student.totalGrades}</td>
                      <td>
                        <div className="passing-rate">
                          <span className="rate-text">{student.passingRate}%</span>
                          <div className="rate-bar">
                            <div 
                              className="rate-fill" 
                              style={{ width: `${student.passingRate}%` }}
                            ></div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <button 
                          className="action-btn small"
                          onClick={() => {
                            setSelectedStudent(student.studentId);
                            setActiveTab('overview');
                          }}
                        >
                          查看詳情
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'statistics' && (
          <div className="statistics-section">
            <h2>統計分析</h2>
            
            <div className="stats-grid">
              <div className="stats-card">
                <h3>整體統計</h3>
                <div className="stats-content">
                  <div className="stat-item">
                    <span className="stat-label">總成績數:</span>
                    <span className="stat-value">{gradesData.totalGrades}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">平均分:</span>
                    <span className="stat-value">{gradesData.averageGrade.toFixed(1)}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">最高分:</span>
                    <span className="stat-value">{gradesData.highestGrade}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">最低分:</span>
                    <span className="stat-value">{gradesData.lowestGrade}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">及格率:</span>
                    <span className="stat-value">
                      {gradesData.totalGrades > 0 
                        ? Math.round((gradesData.passingGrades / gradesData.totalGrades) * 100) 
                        : 0}%
                    </span>
                  </div>
                </div>
              </div>

              <div className="stats-card">
                <h3>科目統計</h3>
                <div className="subject-stats-list">
                  {Object.values(gradesData.subjectGroups).map((group, index) => (
                    <div key={index} className="subject-stat-item">
                      <div className="subject-name">{group.subject}</div>
                      <div className="subject-stats">
                        <span className="stat">{group.totalGrades} 個成績</span>
                        <span className="stat average">{group.averageGrade.toFixed(1)} 平均分</span>
                        <span className="stat rate">{group.passingRate}% 及格率</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="stats-card">
                <h3>班別統計</h3>
                <div className="class-stats-list">
                  {Object.values(gradesData.classGroups).map((group, index) => (
                    <div key={index} className="class-stat-item">
                      <div className="class-name">{group.className}</div>
                      <div className="class-stats">
                        <span className="stat">{group.totalGrades} 個成績</span>
                        <span className="stat average">{group.averageGrade.toFixed(1)} 平均分</span>
                        <span className="stat rate">{group.passingRate}% 及格率</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GradesManagementPage;
